// eslint-disable-next-line no-restricted-imports
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { getEnv } from '@tink/config/src/env';
import { Primitive } from '@sentry/types';

/**
 * Install the Sentry client to capture uncaught exceptions.
 *
 * Events will not be sent from local development, unless `options.debug` is
 * turned on.
 *
 * This will look for `window.SENTRY_RELEASE` to tag events to the proper release,
 * so that source maps can be resolved. The variable is set by the Sentry
 * webpack plugin configured in the build chain.
 *
 * Various "breadcrumbs" will be logged to Sentry: network
 * requests, DOM events, console.log calls etc. These can be seen
 * inside the details for an issue.
 *
 * Performance tracing will also be enabled by default, which will
 * log web vitals metrics (FCP, LCP etc.) and React and browser events
 * such as loading resources, rendering, navigation etc. These can be
 * seen in the Performance tab in the dashboard. Configure the sampling
 * rate via `options.tracesSampleRate`.
 *
 * @param options
 * @param options.debug Setting this to `true` will forcefully enable Sentry
 *   and turn on debug logging.
 */
export function installSentry(options: Sentry.BrowserOptions) {
  const isProdBuild = process.env.NODE_ENV === 'production';
  const { debug = false } = options;

  Sentry.init({
    debug,
    enabled: isProdBuild || debug,
    environment: getEnv(),
    integrations: [new Integrations.BrowserTracing()],
    attachStacktrace: true,

    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/sampling/
    tracesSampleRate: 0.2,

    ...options,
  });
}

type SentryContext = {
  /**
   * Sentry uses one or more "fingerprints" to decide how to group errors into issues.
   *
   * If you wish to append information, thus making the grouping slightly less aggressive,
   * you can do that as well by adding the value SentryFingerprint.DEFAULT in the list.
   *
   * @example
   * // Split up a issues into more groups:
   * fingerprint: [SentryFingerprint.DEFAULT, '/users']
   *
   * @example
   * // Merge several issues into one group:
   * fingerprint: ['404']
   *
   * @see https://docs.sentry.io/platforms/javascript/usage/sdk-fingerprinting/
   * @see https://docs.sentry.io/product/sentry-basics/grouping-and-fingerprints/
   */
  fingerprint?: (SentryFingerprint | string)[];
  /**
   * Tags are key/value pairs assigned to events that can be used for
   * breaking down issues or quick access to finding related events.
   *
   * Tags won't influence how events are grouped, use the fingerprint
   * for that.
   *
   * @see https://docs.sentry.io/platforms/javascript/enriching-events/tags/
   */
  tags?: Record<string, string>;
  /**
   * You can add arbitrary data to events using this.
   *
   * Custom data is not searchable, use tags instead.
   *
   * @see https://docs.sentry.io/platforms/javascript/enriching-events/context/
   */
  custom?: Record<string, string | string[] | Record<string, unknown>>;
};

/**
 * @see https://docs.sentry.io/data-management/event-grouping/sdk-fingerprinting/?platform=javascript
 */
export enum SentryFingerprint {
  // adds the default fingerprint values
  DEFAULT = '{{ default }}',
  // groups by the event’s transaction
  TRANSACTION = '{{ transaction }}',
  // groups by the event’s function name
  FUNCTION = '{{ function }}',
  // groups by the event’s exception type name
  TYPE = '{{ type }}',
  // groups by the event’s module name
  MODULE = '{{ module }}',
  // groups by the event’s package name
  PACKAGE = '{{ package }}',
}

/**
 * Manually log an exception to Sentry.
 *
 * @param e The error you want to log.
 * @param context You can pass in extra information using the context.
 */
export function captureException(e: Error, context: SentryContext = {}) {
  Sentry.captureException(e, {
    tags: context.tags,
    fingerprint: context.fingerprint,
    contexts: context.custom && { custom: context.custom },
  });
}

/**
 * Manually log a message to Sentry.
 *
 * @param msg The message you want to log.
 * @param context You can pass in extra information using the context.
 */
export function captureMessage(msg: string, context: SentryContext = {}) {
  Sentry.captureMessage(msg, {
    tags: context.tags,
    fingerprint: context.fingerprint,
    contexts: context.custom && { custom: context.custom },
  });
}

/**
 * Sets a tag using configureScope. It differs from withScope in the sense that configure-scope
 * actually changes the current active scope, all successive calls to configure-scope will keep the changes
 *
 * @param key The key for the tag.
 * @param value The value you want to set the tag with.
 */
export function setScopeTag(key: string, value: Primitive) {
  Sentry.configureScope((scope) => scope.setTag(key, value));
}

/**
 * @see https://github.com/getsentry/sentry/blob/78369012293b9314f381134297fe1b0e434c0800/static/app/types/breadcrumbs.tsx#L15-L32.
 */
export enum BreadcrumbType {
  INFO = 'info',
  DEBUG = 'debug',
  UI = 'ui',
  USER = 'user',
  WARNING = 'warning',
  ERROR = 'error',
  HTTP = 'http',
  NAVIGATION = 'navigation',
  SESSION = 'session',
  INIT = 'init',
}

/**
 * Create a breadcrumb that will get sent with the next error event.
 *
 * @param message
 * @param type An optional enum that can be used to distinguish the breadcrumb
 *   in the Sentry UI.
 * @param data Optional arbitrary data.
 *
 * @see https://docs.sentry.io/platforms/javascript/enriching-events/breadcrumbs/
 */
export function addBreadcrumb(
  message: string,
  {
    type = BreadcrumbType.INFO,
    data,
  }: {
    type?: BreadcrumbType;
    data?: Record<string, unknown>;
  } = {}
) {
  Sentry.addBreadcrumb({ type, message, data });
}
